import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { SurfaceLayerProvider, useResolvedSurfaceLayer } from "ui/layers";
import { useBodyScrollLock } from "ui/useBodyScrollLock";
import { cn } from "util/cn";

export const FULLSCREEN_VIEWER_BODY_CLASS = "fullscreen-viewer-active";

export interface FullscreenViewerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | null;
  children: React.ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  closeButtonPosition?: "left" | "right";
  hideHeader?: boolean;
}

const ESCAPE_KEY = "Escape";

/**
 * Reusable full-screen viewer shell.
 * Used for GraphFullscreenViewer and MediaViewer.
 * 
 * Toggles a global body class to hide the AppBar and strip top padding.
 */
const FullscreenViewer: React.FC<FullscreenViewerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className,
  headerClassName,
  contentClassName,
  closeButtonPosition = "left",
  hideHeader = false,
}) => {
  const [contentElement, setContentElement] = useState<HTMLElement | null>(null);
  const layer = useResolvedSurfaceLayer({
    tier: "modal",
  });
  const providedLayer = React.useMemo(
    () => ({ ...layer, contentElement }),
    [contentElement, layer]
  );

  useBodyScrollLock(isOpen);

  // Toggle the global body class while the viewer is open
  useEffect(() => {
    if (!isOpen) return;
    const body = document.body;
    const currentCount = Number(body.dataset.fullscreenViewerCount ?? "0");
    const nextCount = currentCount + 1;
    body.dataset.fullscreenViewerCount = String(nextCount);
    body.classList.add(FULLSCREEN_VIEWER_BODY_CLASS);
    
    return () => {
      const count = Number(body.dataset.fullscreenViewerCount ?? "1");
      const next = Math.max(count - 1, 0);
      if (next === 0) {
        delete body.dataset.fullscreenViewerCount;
        body.classList.remove(FULLSCREEN_VIEWER_BODY_CLASS);
      } else {
        body.dataset.fullscreenViewerCount = String(next);
      }
    };
  }, [isOpen]);

  // Escape key dismisses the viewer.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === ESCAPE_KEY) {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const content = (
    <div
      ref={setContentElement}
      className={cn("fixed inset-0 flex flex-col bg-black dark:bg-black", className)}
      style={{
        zIndex: layer.base,
        height: "100%",
        maxHeight: "100dvh",
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title || "Fullscreen Viewer"}
      data-fullscreen-viewer="true"
    >
      <SurfaceLayerProvider value={providedLayer}>
        {!hideHeader && (
          <div className={cn("flex flex-shrink-0 items-center gap-2 border-b border-eoe-border-main px-3 py-2 dark:border-eoe-dark-border-main", headerClassName)}>
            {closeButtonPosition === "left" && (
              <button
                type="button"
                onClick={onClose}
                className="flex size-9 items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/20"
                aria-label="Close"
              >
                <X className="size-5" aria-hidden />
              </button>
            )}
            <span className="flex-1 truncate text-sm font-semibold">
              {title}
            </span>
            {closeButtonPosition === "right" && (
              <button
                type="button"
                onClick={onClose}
                className="flex size-9 items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/20"
                aria-label="Close"
              >
                <X className="size-5" aria-hidden />
              </button>
            )}
          </div>
        )}
        <div className={cn("relative min-h-0 flex-1", contentClassName)}>
          {children}
        </div>
      </SurfaceLayerProvider>
    </div>
  );

  return createPortal(content, document.body);
};

export default FullscreenViewer;
